import React, { useState, useEffect } from 'react';
import { Shield, Plus, Trash2, Edit2, Users, Check, X, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PERMISSIONS = [
  { id: 'users', name: 'User Management', description: 'View and manage users' },
  { id: 'users_view', name: 'View Users Only', description: 'View users without editing' },
  { id: 'reports', name: 'User Reports', description: 'Handle reported content' },
  { id: 'messages', name: 'Support Messages', description: 'Respond to user support' },
  { id: 'payments', name: 'Payment Settings', description: 'Configure payments' },
  { id: 'content', name: 'Content Management', description: 'Edit pages and content' },
  { id: 'settings', name: 'Site Settings', description: 'Change site configuration' },
  { id: 'announcements', name: 'Announcements', description: 'Create announcements' },
  { id: 'verifications', name: 'ID Verification', description: 'Verify user identities' },
  { id: 'approvals', name: 'Account Approvals', description: 'Approve new accounts' },
  { id: 'all', name: 'Full Access', description: 'Access everything' },
];

const AdminRoles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: []
  });
  const { toast } = useToast();

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/extended/roles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRoles(data.roles || []);
      }
    } catch (error) {
      console.error('Failed to load roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || formData.permissions.length === 0) {
      toast({ title: "Error", description: "Please fill name and select permissions", variant: "destructive" });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const url = editingRole 
        ? `${API_URL}/api/admin/extended/roles/${editingRole.id}`
        : `${API_URL}/api/admin/extended/roles`;
      
      const response = await fetch(url, {
        method: editingRole ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast({ title: "Success", description: editingRole ? "Role updated" : "Role created" });
        setShowForm(false);
        setEditingRole(null);
        resetForm();
        loadRoles();
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to save role", variant: "destructive" });
    }
  };

  const handleDelete = async (roleId) => {
    if (!window.confirm('Delete this role?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/extended/roles/${roleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({ title: "Deleted" });
        loadRoles();
      } else {
        const error = await response.json();
        toast({ title: "Error", description: error.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
    }
  };

  const togglePermission = (permId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permId)
        ? prev.permissions.filter(p => p !== permId)
        : [...prev.permissions, permId]
    }));
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', permissions: [] });
  };

  const startEdit = (role) => {
    if (role.is_system) {
      toast({ title: "Cannot Edit", description: "System roles cannot be modified", variant: "destructive" });
      return;
    }
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      permissions: role.permissions || []
    });
    setShowForm(true);
  };

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Admin Roles</h1>
          <p className="text-gray-600">Manage admin roles and permissions</p>
        </div>
        <Button onClick={() => { resetForm(); setEditingRole(null); setShowForm(true); }} className="bg-mint-500 hover:bg-mint-600">
          <Plus className="w-4 h-4 mr-2" /> Create Role
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editingRole ? 'Edit Role' : 'New Role'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X /></Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Role Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., Moderator, Support Agent"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                  placeholder="Brief description of this role"
                />
              </div>

              <div className="space-y-2">
                <Label>Permissions *</Label>
                <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto p-2 border rounded-lg">
                  {PERMISSIONS.map((perm) => (
                    <button
                      key={perm.id}
                      onClick={() => togglePermission(perm.id)}
                      className={`flex items-center gap-2 p-2 rounded-lg text-left text-sm transition-colors ${
                        formData.permissions.includes(perm.id)
                          ? 'bg-mint-100 border-mint-300 border'
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded flex items-center justify-center ${
                        formData.permissions.includes(perm.id) ? 'bg-mint-500 text-white' : 'border'
                      }`}>
                        {formData.permissions.includes(perm.id) && <Check className="w-3 h-3" />}
                      </div>
                      <div>
                        <p className="font-medium">{perm.name}</p>
                        <p className="text-xs text-gray-500">{perm.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowForm(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleSubmit} className="flex-1 bg-mint-500 hover:bg-mint-600">
                  {editingRole ? 'Update' : 'Create'} Role
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Roles List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map((role) => (
          <Card key={role.id} className={role.is_system ? 'border-blue-200 bg-blue-50/50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Shield className={`w-5 h-5 ${role.is_system ? 'text-blue-600' : 'text-gray-600'}`} />
                  <h3 className="font-semibold">{role.name}</h3>
                </div>
                {!role.is_system && (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(role)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(role.id)} className="text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
              
              {role.description && (
                <p className="text-sm text-gray-500 mb-3">{role.description}</p>
              )}
              
              <div className="flex flex-wrap gap-1">
                {role.permissions?.slice(0, 4).map((perm) => (
                  <span key={perm} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                    {PERMISSIONS.find(p => p.id === perm)?.name || perm}
                  </span>
                ))}
                {role.permissions?.length > 4 && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                    +{role.permissions.length - 4} more
                  </span>
                )}
              </div>
              
              {role.is_system && (
                <p className="text-xs text-blue-600 mt-2">System role (cannot be modified)</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminRoles;
